import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Check, Star, Crown, Sparkles, Loader2, ArrowRight, 
  Home, Shield, Zap, MessageSquare, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { subscriptionsApi } from '../services/api';
import { toast } from 'sonner';
import { usePageMetadata } from '../hooks/usePageMetadata';

const TIER_ICONS = {
  standard: Home,
  featured: Star,
  concierge: Crown
};

const TIER_COLORS = {
  standard: 'border-slate-200 hover:border-trust-blue',
  featured: 'border-amber-300 hover:border-amber-400 bg-gradient-to-b from-amber-50 to-white',
  concierge: 'border-trust-blue hover:border-trust-blue-hover bg-gradient-to-b from-blue-50 to-white'
};

const PricingCard = ({ tier, isCurrentTier, isLoading, onSelect }) => {
  const Icon = TIER_ICONS[tier.tier] || Home;
  const isPopular = tier.tier === 'featured';
  const isPremium = tier.tier === 'concierge';
  
  return (
    <div 
      className={`relative rounded-2xl border-2 p-6 transition-all duration-300 ${TIER_COLORS[tier.tier]} ${
        isPopular ? 'scale-105 shadow-xl z-10' : 'shadow-md'
      }`}
      data-testid={`pricing-card-${tier.tier}`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <Badge className="bg-amber-500 text-white px-4 py-1">
            <Sparkles className="w-3 h-3 mr-1" />
            Most Popular
          </Badge>
        </div>
      )}
      
      {isPremium && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <Badge className="bg-trust-blue text-white px-4 py-1">
            <Shield className="w-3 h-3 mr-1" />
            Premium
          </Badge>
        </div>
      )}
      
      <div className="text-center mb-6">
        <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4 ${
          tier.tier === 'standard' ? 'bg-slate-100' :
          tier.tier === 'featured' ? 'bg-amber-100' :
          'bg-trust-blue/10'
        }`}>
          <Icon className={`w-7 h-7 ${
            tier.tier === 'standard' ? 'text-slate-600' :
            tier.tier === 'featured' ? 'text-amber-600' :
            'text-trust-blue'
          }`} />
        </div>
        
        <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
          {tier.name}
        </h3>
        
        <div className="flex items-baseline justify-center gap-1">
          <span className="font-heading text-4xl font-bold text-slate-900">
            ${tier.price}
          </span>
          <span className="text-slate-500">/month</span>
        </div>
      </div>
      
      <ul className="space-y-3 mb-6">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className={`w-5 h-5 mt-0.5 shrink-0 ${
              tier.tier === 'standard' ? 'text-green-500' :
              tier.tier === 'featured' ? 'text-amber-500' :
              'text-trust-blue'
            }`} />
            <span className="text-slate-600 text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      
      {tier.featured_badge && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-amber-50 rounded-lg">
          <Star className="w-4 h-4 text-amber-500" />
          <span className="text-xs text-amber-700 font-medium">Featured badge included</span>
        </div>
      )}
      
      {tier.concierge && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-blue-50 rounded-lg">
          <Shield className="w-4 h-4 text-trust-blue" />
          <span className="text-xs text-trust-blue font-medium">Concierge support included</span>
        </div>
      )}
      
      <Button
        className={`w-full h-12 ${
          isCurrentTier 
            ? 'bg-green-500 hover:bg-green-600' 
            : tier.tier === 'standard' 
              ? 'bg-slate-800 hover:bg-slate-900'
              : tier.tier === 'featured'
                ? 'bg-amber-500 hover:bg-amber-600'
                : 'bg-trust-blue hover:bg-trust-blue-hover'
        } text-white`}
        onClick={() => onSelect(tier.tier)}
        disabled={isLoading || isCurrentTier}
        data-testid={`select-${tier.tier}-btn`}
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isCurrentTier ? (
          <>
            <Check className="w-5 h-5 mr-2" />
            Current Plan
          </>
        ) : (
          <>
            Choose {tier.name.split(' ')[0]}
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  
  // Apply page metadata
  usePageMetadata();
  
  const returnTo = searchParams.get('return') || '/dashboard';
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const tiersRes = await subscriptionsApi.getTiers();
      setTiers(tiersRes.data.tiers);
      
      if (isAuthenticated && user?.role === 'owner') {
        const subRes = await subscriptionsApi.getMySubscription();
        setCurrentSubscription(subRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch pricing data:', error);
      toast.error('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectTier = async (tier) => {
    if (!isAuthenticated) {
      // Redirect to register as owner
      navigate(`/register?role=owner&return=/pricing`);
      return;
    }
    
    if (user?.role !== 'owner') {
      toast.error('Please register as a property owner to subscribe');
      navigate('/register?role=owner&return=/pricing');
      return;
    }
    
    // If already subscribed, change tier instead
    if (currentSubscription?.status === 'active' && currentSubscription?.tier !== tier) {
      try {
        setCheckoutLoading(tier);
        await subscriptionsApi.changeTier(tier);
        toast.success(`Subscription changed to ${tier} plan!`);
        fetchData();
      } catch (error) {
        toast.error('Failed to change subscription tier');
      } finally {
        setCheckoutLoading(null);
      }
      return;
    }
    
    // Create new subscription checkout
    setCheckoutLoading(tier);
    try {
      const res = await subscriptionsApi.createCheckout(tier, window.location.origin);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="bg-soft-gray min-h-screen py-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="bg-trust-blue/10 text-trust-blue mb-4">
              <Zap className="w-3 h-3 mr-1" />
              Owner Subscriptions
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Choose Your Listing Plan
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              No commission fees. Just a simple monthly subscription to list your property
              and connect with quality renters seeking 30+ day stays.
            </p>
          </div>
          
          {/* Benefits Row */}
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center gap-2 text-slate-600">
              <Check className="w-5 h-5 text-green-500" />
              <span>No transaction fees</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Check className="w-5 h-5 text-green-500" />
              <span>Unlimited bookings</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Check className="w-5 h-5 text-green-500" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <MessageSquare className="w-5 h-5 text-trust-blue" />
              <span>Direct messaging</span>
            </div>
          </div>
          
          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto mb-16">
            {tiers.map((tier) => (
              <PricingCard
                key={tier.tier}
                tier={tier}
                isCurrentTier={currentSubscription?.tier === tier.tier && currentSubscription?.status === 'active'}
                isLoading={checkoutLoading === tier.tier}
                onSelect={handleSelectTier}
              />
            ))}
          </div>
          
          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-center text-slate-900 mb-8">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-2">
                  How does billing work?
                </h3>
                <p className="text-slate-600">
                  Your subscription renews monthly until you cancel. You can upgrade, downgrade, 
                  or cancel anytime. Changes take effect immediately with prorated billing.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-2">
                  What happens if I cancel?
                </h3>
                <p className="text-slate-600">
                  If you cancel, your listing will become inactive at the end of your billing period.
                  Your listing data is preserved, and you can reactivate anytime by subscribing again.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-2">
                  Can I change plans?
                </h3>
                <p className="text-slate-600">
                  Yes! You can upgrade or downgrade at any time. When upgrading, you'll be charged 
                  a prorated amount for the remainder of your billing period. Downgrades take effect 
                  on your next billing date.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-2">
                  What's included in the Concierge tier?
                </h3>
                <p className="text-slate-600">
                  Concierge subscribers receive hands-on support from our team, including listing 
                  quality review, photography tips, and priority customer support. It's perfect 
                  for owners who want the best visibility and professional assistance.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-2">
                  Do renters pay any fees?
                </h3>
                <p className="text-slate-600">
                  <strong>No membership fees for renters.</strong> Renters only pay the rental amount 
                  (and any deposit) directly through our secure platform. All payments are processed 
                  through Stripe for maximum security.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-2">
                  What are the payment options for renters?
                </h3>
                <p className="text-slate-600 mb-3">
                  Renters can choose between two payment options:
                </p>
                <ul className="text-slate-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span><strong>Pay Full Amount:</strong> Pay 100% upfront for instant confirmation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span><strong>Pay Deposit Only:</strong> Pay a deposit now, remaining balance before check-in (deposit is non-refundable)</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 shadow-sm border border-purple-100">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-purple-600" />
                  Tier-Based Payment Controls (For Owners)
                </h3>
                <div className="text-slate-600 space-y-3">
                  <p className="text-sm bg-white/60 p-3 rounded-lg">
                    <strong className="text-slate-800">Standard & Featured Tiers:</strong><br />
                    Must allow renters to choose between deposit-only or full payment.
                  </p>
                  <p className="text-sm bg-white/60 p-3 rounded-lg">
                    <strong className="text-purple-800">Concierge Tier (Premium):</strong><br />
                    Can optionally <strong>require full payment upfront</strong> — removing the deposit option entirely.
                  </p>
                </div>
              </div>

              {/* Owner-Only Policy */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 shadow-sm border border-amber-200">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" />
                  Owner-Only Listings Policy
                </h3>
                <p className="text-slate-600 mb-4">
                  Stay Pair Homes is exclusively for <strong>direct property owners</strong>. This is a core part of our platform's value proposition:
                </p>
                <ul className="text-slate-600 space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <span>Property management companies and agencies are <strong>not permitted</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <span>Owners must complete an attestation confirming they own the property</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <span>Renters can report suspected agencies for our team to review</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <span>Violations result in listing removal and account suspension</span>
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <p className="text-amber-700 text-sm">
                    <strong>Why this matters:</strong> Direct owner-renter connections mean no middleman fees, 
                    faster responses, and more personalized service for renters.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Trust Signals */}
          <div className="text-center mt-16">
            <div className="flex items-center justify-center gap-2 text-slate-500 mb-4">
              <Shield className="w-5 h-5" />
              <span>Secure payments powered by Stripe</span>
            </div>
            <p className="text-sm text-slate-400">
              Stay Pair Homes connects property owners with quality renters in Los Angeles County.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PricingPage;
